import type { INavigationControlIntegrationApi, INavigationController } from './interfaces';

export enum NavigationsDirection {
  PanUp = 'pan-up',
  PanRight = 'pan-right',
  PanDown = 'pan-down',
  PanLeft = 'pan-left',
  ZoomIn = 'zoom-in',
  ZoomOut = 'zoom-out',
  RotateUp = 'rotate-up',
  RotateRight = 'rotate-right',
  RotateDown = 'rotate-down',
  RotateLeft = 'rotate-left',
  ResetCamera = 'reset-camera'
}

export enum ResetCameraStrategy {
  CentrifyOnly = 'centrify-only',
  CentrifyAndDefaultOrientation = 'centrify-and-default-orientation'
}

class _Animator {
  private _id?: number;

  public constructor(action: (fraction: number) => void, speed: number) {
    let actionFraction = 0;

    let animationStart = Date.now();

    const animate = (): void => {
      action(actionFraction);
      const animationFinish = Date.now(); // just after action finished
      const elapsed = animationFinish - animationStart;
      const fps = 1000 / elapsed;
      actionFraction = speed / fps;
      animationStart = animationFinish; // just before new animation request
      this._id = requestAnimationFrame(animate);
    };

    this._id = requestAnimationFrame(animate);
  }

  public stop(): void {
    if (this._id !== undefined) {
      cancelAnimationFrame(this._id);
      this._id = undefined;
    }
  }
}

export class NavigationController implements INavigationController {
  private _animation?: _Animator;

  // rad/sec
  private readonly _rotationSpeed = (Math.PI * 2) / 5; // full circle in 5 seconds

  // mm/sec
  private readonly _panSpeed = 15;

  // times/sec
  private readonly _zoomSpeed = 0.3;

  private readonly _integrationApi: INavigationControlIntegrationApi;

  private _resetCameraStrategy: ResetCameraStrategy = ResetCameraStrategy.CentrifyOnly;

  public constructor(integrationApi: INavigationControlIntegrationApi) {
    this._integrationApi = integrationApi;
  }

  public setResetCameraStrategy(strategy: ResetCameraStrategy): void {
    this._resetCameraStrategy = strategy;
  }

  public onInteractionStarted(direction: NavigationsDirection): void {
    if (this._animation) {
      throw new Error('Another animation has not ben finished yet');
    }

    const directionVector = this._integrationApi.getCameraDirection();
    const topVector = this._integrationApi.getCameraTop();
    const centerPoint = this._integrationApi.getCenterPoint();

    switch (direction) {
      case NavigationsDirection.ZoomIn:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.zoomCamera(1 + fraction),
          this._zoomSpeed
        );
        break;
      case NavigationsDirection.ZoomOut:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.zoomCamera(1 - fraction),
          this._zoomSpeed
        );
        break;
      case NavigationsDirection.PanUp:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.panCamera(topVector.clone().negate().multiplyScalar(fraction)),
          this._panSpeed
        );
        break;
      case NavigationsDirection.PanDown:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.panCamera(topVector.clone().multiplyScalar(fraction)),
          this._panSpeed
        );
        break;
      case NavigationsDirection.PanRight:
        this._animation = new _Animator(
          (fraction: number) =>
            this._integrationApi.panCamera(topVector.clone().cross(directionVector).multiplyScalar(fraction)),
          this._panSpeed
        );
        break;
      case NavigationsDirection.PanLeft:
        this._animation = new _Animator(
          (fraction: number) =>
            this._integrationApi.panCamera(directionVector.clone().cross(topVector).multiplyScalar(fraction)),
          this._panSpeed
        );
        break;
      case NavigationsDirection.RotateUp:
        this._animation = new _Animator(
          (fraction: number) =>
            this._integrationApi.rotateCamera(centerPoint, directionVector.clone().cross(topVector), fraction),
          this._rotationSpeed
        );
        break;
      case NavigationsDirection.RotateDown:
        this._animation = new _Animator(
          (fraction: number) =>
            this._integrationApi.rotateCamera(centerPoint, directionVector.clone().cross(topVector), -fraction),
          this._rotationSpeed
        );
        break;
      case NavigationsDirection.RotateRight:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.rotateCamera(centerPoint, topVector, -fraction),
          this._rotationSpeed
        );
        break;
      case NavigationsDirection.RotateLeft:
        this._animation = new _Animator(
          (fraction: number) => this._integrationApi.rotateCamera(centerPoint, topVector, fraction),
          this._rotationSpeed
        );
        break;
      case NavigationsDirection.ResetCamera:
        this._integrationApi.resetCamera(
          this._resetCameraStrategy === ResetCameraStrategy.CentrifyAndDefaultOrientation
        );
        break;
      default:
        throw new Error('Unknown direction');
    }
  }

  public onInteractionEnded(): void {
    this._animation?.stop();
    this._animation = undefined;
  }
}
